import { createClient } from '@supabase/supabase-js';
import type { Faculty, Subject, Lesson, LessonStateCount, RawQuestion, NormalizedQuestion, NormalizedOption } from './types';

const DEFAULT_SUPABASE_URL = 'https://rnuwvxwvmzcvwprpjuxo.supabase.co';
const DEFAULT_SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJudXd2eHd2bXpjdndwcnBqdXhvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTM0NzU4ODUsImV4cCI6MjA2OTA1MTg4NX0.vclbS_HnHEXfqqMhhzvd-9iHTacEUx-zIOyqjTL1KJk';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || DEFAULT_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || DEFAULT_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Normalizes options from jsonb defensively.
 * Accepts array of strings or array of objects with text/label/value.
 */
export function normalizeOptions(raw: unknown): NormalizedOption[] {
  if (!raw || !Array.isArray(raw)) {
    return [];
  }

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  return raw.map((item, index) => {
    let text = '';
    if (typeof item === 'string') {
      text = item;
    } else if (item && typeof item === 'object') {
      const obj = item as Record<string, unknown>;
      text = String(obj.text || obj.label || obj.value || obj.content || '');
    } else {
      text = String(item ?? '');
    }

    const letter = alphabet[index] || String(index + 1);

    return {
      index,
      letter,
      text: text.trim(),
    };
  });
}

/**
 * Normalizes a raw question record into a safe, strongly-typed model.
 */
export function normalizeQuestion(raw: RawQuestion): NormalizedQuestion {
  const options = normalizeOptions(raw.options);
  const maxSelections = raw.max_selections && raw.max_selections > 0 ? raw.max_selections : 1;
  const answerIndices = Array.isArray(raw.answer_indices)
    ? raw.answer_indices.map((idx) => Number(idx)).filter((idx) => !isNaN(idx))
    : [];

  return {
    id: raw.id,
    state: raw.state || '',
    year: raw.year || null,
    chapter: raw.chapter || null,
    question_text: raw.question_text || '',
    max_selections: Math.max(maxSelections, answerIndices.length > 1 ? answerIndices.length : 1),
    image_url: raw.image_url || null,
    options,
    answer_indices: answerIndices,
    question_order: raw.question_order ?? null,
    faculty_id: raw.faculty_id ?? null,
    lesson_id: raw.lesson_id ?? null,
    explanation: raw.explanation || null,
  };
}

/**
 * Loads metadata once on app start: faculties, subjects, lessons (has_theory, no theory text), and counts.
 */
export async function loadInitialMetadata(): Promise<{
  faculties: Faculty[];
  subjects: Subject[];
  lessons: Lesson[];
  counts: LessonStateCount[];
}> {
  const [facultiesRes, subjectsRes, lessonsRes, countsRes] = await Promise.all([
    supabase
      .from('faculties')
      .select('id, slug, name_ar, name_fr, name_en, years_count, sort_order')
      .order('sort_order', { ascending: true }),
    supabase.from('subjects').select('id, name, department, faculty_id'),
    supabase
      .from('lessons')
      .select('id, subject_id, name, sort_order, has_theory')
      .order('sort_order', { ascending: true }),
    supabase.from('v_lesson_state_counts').select('lesson_id, state, n'),
  ]);

  if (facultiesRes.error) throw facultiesRes.error;
  if (subjectsRes.error) throw subjectsRes.error;
  if (lessonsRes.error) throw lessonsRes.error;
  if (countsRes.error) throw countsRes.error;

  return {
    faculties: (facultiesRes.data || []) as Faculty[],
    subjects: (subjectsRes.data || []) as Subject[],
    lessons: (lessonsRes.data || []) as Lesson[],
    counts: (countsRes.data || []) as LessonStateCount[],
  };
}

/**
 * Fetches questions for a single lesson or entire subject.
 * Paginates with .range() in chunks of 1000 until a chunk returns fewer than 1000 rows.
 */
export async function fetchQuestions(params: {
  lessonId: string; // 'all' or lesson UUID
  subjectId?: string; // required if lessonId === 'all'
  lessonIds?: string[]; // all lesson IDs for the subject if lessonId === 'all'
  wilaya: string; // wilaya name or 'all'
}): Promise<NormalizedQuestion[]> {
  const { lessonId, lessonIds, wilaya } = params;
  const chunkSize = 1000;
  let offset = 0;
  const allRows: RawQuestion[] = [];
  let keepFetching = true;

  while (keepFetching) {
    let query = supabase
      .from('all_questions')
      .select(
        'id, state, grade, year, subject, chapter, question_text, max_selections, image_url, options, answer_indices, question_order, faculty_id, lesson_id, explanation, created_at'
      )
      .order('question_order', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: true })
      .range(offset, offset + chunkSize - 1);

    if (lessonId === 'all') {
      if (lessonIds && lessonIds.length > 0) {
        query = query.in('lesson_id', lessonIds);
      }
    } else {
      query = query.eq('lesson_id', lessonId);
    }

    if (wilaya && wilaya !== 'all') {
      query = query.eq('state', wilaya);
    }

    const { data, error } = await query;
    if (error) {
      throw error;
    }

    const rows = (data || []) as RawQuestion[];
    allRows.push(...rows);

    if (rows.length < chunkSize) {
      keepFetching = false;
    } else {
      offset += chunkSize;
    }
  }

  return allRows.map(normalizeQuestion);
}

/**
 * Fetches theory content separately for a single lesson.
 */
export async function fetchLessonTheory(lessonId: string): Promise<{ name: string; theory: string | null }> {
  const { data, error } = await supabase
    .from('lessons')
    .select('name, theory')
    .eq('id', lessonId)
    .single();

  if (error) {
    throw error;
  }

  return {
    name: data.name,
    theory: data.theory || null,
  };
}

/**
 * Fetches distinct exam years that have questions for a given subject/wilaya.
 * Years without questions (e.g. 2023 with 0 questions) are excluded.
 */
export async function fetchSubjectYears(params: {
  lessonIds?: string[];
  subjectName?: string;
  wilaya?: string | null;
}): Promise<string[]> {
  const { lessonIds, subjectName, wilaya } = params;

  if ((!lessonIds || lessonIds.length === 0) && !subjectName) {
    return [];
  }

  let query = supabase
    .from('all_questions')
    .select('year')
    .not('year', 'is', null);

  if (lessonIds && lessonIds.length > 0) {
    query = query.in('lesson_id', lessonIds);
  } else if (subjectName) {
    query = query.eq('subject', subjectName);
  }

  if (wilaya && wilaya !== 'all') {
    query = query.eq('state', wilaya);
  }

  const { data, error } = await query;
  if (error || !data) {
    console.error('Failed to fetch subject exam years:', error);
    return [];
  }

  const yearSet = new Set<string>();
  data.forEach((row: { year?: string | null }) => {
    if (row.year) {
      const trimmed = String(row.year).trim();
      if (/^\d{4}$/.test(trimmed)) {
        yearSet.add(trimmed);
      }
    }
  });

  return Array.from(yearSet).sort((a, b) => Number(b) - Number(a));
}

